import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { 
  TrendingUp, BarChart3, Briefcase, Settings, LogOut, Globe, Brain, Shield,
  CreditCard, Calendar, AlertTriangle, CheckCircle2, XCircle, HelpCircle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

import API from '../utils/api';

const SettingsPage = () => {
  const { t, language, setLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchSubscriptionInfo();
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await fetch(`${API}/subscription/info`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionInfo(data);
      } else {
        // If API fails, set default inactive subscription info
        console.warn('Failed to fetch subscription info, using defaults');
        setSubscriptionInfo({
          plan: user?.subscription_plan || 'basic',
          status: user?.subscription_status || 'inactive',
          created_at: null
        });
      }
    } catch (error) {
      console.error('Error fetching subscription info:', error);
      // Set default subscription info on error
      setSubscriptionInfo({
        plan: user?.subscription_plan || 'basic',
        status: user?.subscription_status || 'inactive',
        created_at: null
      });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCancelSubscription = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/subscription/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        setShowCancelModal(false);
        fetchSubscriptionInfo();
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/subscription/reactivate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        fetchSubscriptionInfo();
      }
    } catch (error) {
      console.error('Error reactivating subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    const expectedConfirm = language === 'de' ? 'LÖSCHEN' : 'DELETE';
    if (deleteConfirmText.trim() !== expectedConfirm) {
      setDeleteError(language === 'de' ? `Bitte tippe "${expectedConfirm}" zur Bestätigung ein` : `Please type "${expectedConfirm}" to confirm`);
      return;
    }
    setDeleteLoading(true);
    try {
      const isOAuthOnly = !!user?.oauth_provider && !user?.has_password;
      const body = isOAuthOnly ? { confirm_oauth: true } : { password: deletePassword };
      const response = await fetch(`${API}/auth/me`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setDeleteError(err.detail || (language === 'de' ? 'Löschen fehlgeschlagen' : 'Deletion failed'));
        setDeleteLoading(false);
        return;
      }
      setShowDeleteModal(false);
      // Logout & redirect to landing
      try { await logout(); } catch (_) { /* noop: continue with redirect */ }
      navigate('/?account_deleted=1', { replace: true });
    } catch (e) {
      setDeleteError(language === 'de' ? 'Netzwerkfehler' : 'Network error');
      setDeleteLoading(false);
    }
  };

  const getPlanName = (plan) => {
    const names = {
      basic: 'Basic',
      pro: 'Pro',
      premium: 'Premium'
    };
    return names[plan] || plan;
  };

  const isAdmin = user?.is_admin === true;
  const isPremium = user?.subscription_plan === 'premium' || isAdmin;
  const isCancelled = subscriptionInfo?.status === 'cancelled';
  const isLifetime = subscriptionInfo?.status === 'lifetime' || isAdmin;
  const isActive = subscriptionInfo?.status === 'active' || isLifetime;
  const isInactive = !isActive && !isCancelled && !isAdmin;

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background flex">
        {/* Sidebar */}
        <Sidebar user={user} onLogout={handleLogout} />

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-4xl mx-auto space-y-6">
            <div>
              <h1 className="text-3xl font-heading font-bold mb-2">{t('settings')}</h1>
              <p className="text-muted-foreground">{language === 'de' ? 'Verwalten Sie Ihr Konto und Ihre Einstellungen' : 'Manage your account and preferences'}</p>
            </div>

            {/* Profile Card */}
            <Card className="glass">
              <CardHeader>
                <CardTitle>{t('profile')}</CardTitle>
                <CardDescription>{language === 'de' ? 'Ihre Kontoinformationen' : 'Your account information'}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{language === 'de' ? 'Name' : 'Name'}</Label>
                  <Input value={user?.name || ''} disabled className="mt-1" />
                </div>
                <div>
                  <Label>E-Mail</Label>
                  <Input value={user?.email || ''} disabled className="mt-1" />
                </div>
              </CardContent>
            </Card>

            {/* Language Card */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  {language === 'de' ? 'Sprache' : 'Language'}
                </CardTitle>
                <CardDescription>{language === 'de' ? 'Wählen Sie Ihre bevorzugte Sprache' : 'Choose your preferred language'}</CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Subscription Card */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  {language === 'de' ? 'Abonnement' : 'Subscription'}
                </CardTitle>
                <CardDescription>{language === 'de' ? 'Verwalten Sie Ihr Abonnement' : 'Manage your subscription'}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {subscriptionInfo && (
                  <>
                    {/* Current Plan */}
                    <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-800/50">
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'de' ? 'Aktueller Plan' : 'Current Plan'}</p>
                        <p className="text-lg font-semibold">{getPlanName(subscriptionInfo.plan)}</p>
                      </div>
                      {isAdmin ? (
                        <div className="flex items-center gap-2 text-amber-400">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="text-sm font-medium">Admin · Vollzugriff</span>
                        </div>
                      ) : isCancelled ? (
                        <div className="flex items-center gap-2 text-rose-400">
                          <XCircle className="h-5 w-5" />
                          <span className="text-sm font-medium">{language === 'de' ? 'Gekündigt' : 'Cancelled'}</span>
                        </div>
                      ) : isLifetime ? (
                        <div className="flex items-center gap-2 text-amber-400">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="text-sm font-medium">Lifetime</span>
                        </div>
                      ) : isActive ? (
                        <div className="flex items-center gap-2 text-emerald-400">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="text-sm font-medium">{language === 'de' ? 'Aktiv' : 'Active'}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-amber-400">
                          <AlertTriangle className="h-5 w-5" />
                          <span className="text-sm font-medium">{language === 'de' ? 'Inaktiv' : 'Inactive'}</span>
                        </div>
                      )}
                    </div>

                    {/* Inactive Plan Notice */}
                    {isInactive && (
                      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5" />
                          <div>
                            <p className="text-amber-400 font-medium">
                              {language === 'de' ? 'Kein aktives Abonnement' : 'No active subscription'}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {language === 'de' 
                                ? 'Schließen Sie ein Abonnement ab, um alle Funktionen freizuschalten.' 
                                : 'Subscribe to unlock all features.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Subscription Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 rounded-lg bg-zinc-800/30">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Calendar className="h-4 w-4" />
                          <span className="text-xs">{language === 'de' ? 'Registriert am' : 'Registered on'}</span>
                        </div>
                        <p className="text-sm font-medium">{formatDate(subscriptionInfo.created_at)}</p>
                      </div>

                      {isCancelled && subscriptionInfo.subscription_end_date && (
                        <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30">
                          <div className="flex items-center gap-2 text-rose-400 mb-1">
                            <AlertTriangle className="h-4 w-4" />
                            <span className="text-xs">{language === 'de' ? 'Endet am' : 'Ends on'}</span>
                          </div>
                          <p className="text-sm font-medium text-rose-400">{formatDate(subscriptionInfo.subscription_end_date)}</p>
                        </div>
                      )}

                      {isCancelled && subscriptionInfo.cancellation_date && (
                        <div className="p-3 rounded-lg bg-zinc-800/30">
                          <div className="flex items-center gap-2 text-muted-foreground mb-1">
                            <Calendar className="h-4 w-4" />
                            <span className="text-xs">{language === 'de' ? 'Kündigungsdatum' : 'Cancellation Date'}</span>
                          </div>
                          <p className="text-sm font-medium">{formatDate(subscriptionInfo.cancellation_date)}</p>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3 pt-2">
                      {/* Show Upgrade/Jetzt Abonnieren for inactive or non-premium users */}
                      {(isInactive || (!isPremium && isActive)) && !isCancelled && (
                        <Button onClick={() => navigate('/subscription')} className="glow-primary">
                          <CreditCard className="h-4 w-4 mr-2" />
                          {isInactive 
                            ? (language === 'de' ? 'Jetzt Abonnieren' : 'Subscribe Now')
                            : 'Upgrade'}
                        </Button>
                      )}

                      {isPremium && isActive && !isCancelled && !isLifetime && (
                        <Button variant="outline" onClick={() => navigate('/subscription')}>
                          {language === 'de' ? 'Verwalten' : 'Manage'}
                        </Button>
                      )}

                      {/* Only show cancel button if user has active subscription (not lifetime) */}
                      {isActive && !isCancelled && !isLifetime && (
                        <Button variant="outline" onClick={() => setShowCancelModal(true)} className="text-rose-400 hover:text-rose-300 border-rose-400/30">
                          {language === 'de' ? 'Abo kündigen' : 'Cancel Subscription'}
                        </Button>
                      )}

                      {isCancelled && (
                        <Button onClick={handleReactivateSubscription} disabled={loading} className="glow-primary">
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          {loading ? (language === 'de' ? 'Wird reaktiviert...' : 'Reactivating...') : (language === 'de' ? 'Reaktivieren' : 'Reactivate')}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Danger Zone - Account Deletion */}
            <Card className="glass border-rose-500/30" data-testid="danger-zone-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-rose-400">
                  <AlertTriangle className="h-5 w-5" />
                  {language === 'de' ? 'Gefahrenzone' : 'Danger Zone'}
                </CardTitle>
                <CardDescription>
                  {language === 'de'
                    ? 'Endgültige Aktionen, die nicht rückgängig gemacht werden können'
                    : 'Permanent actions that cannot be undone'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border border-rose-500/20 bg-rose-500/5">
                  <div>
                    <p className="font-medium text-rose-300">
                      {language === 'de' ? 'Account löschen' : 'Delete account'}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {language === 'de'
                        ? 'Löscht deinen Account und alle Daten (Trades, Konten, Tickets, Journals, KI-Analysen) unwiderruflich.'
                        : 'Permanently deletes your account and all data (trades, accounts, tickets, journals, AI analyses).'}
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => { setShowDeleteModal(true); setDeletePassword(''); setDeleteConfirmText(''); setDeleteError(''); }}
                    data-testid="open-delete-account-btn"
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    {language === 'de' ? 'Account löschen' : 'Delete account'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>

        {/* Cancel Subscription Modal */}
        <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
          <DialogContent className="glass max-w-md">
            <DialogHeader>
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-rose-500/20 border border-rose-500/30">
                <AlertTriangle className="h-8 w-8 text-rose-400" />
              </div>
              <DialogTitle className="text-center text-rose-400">
                {language === 'de' ? 'Abonnement kündigen?' : 'Cancel Subscription?'}
              </DialogTitle>
              <DialogDescription className="text-center">
                {language === 'de' 
                  ? 'Ihr Abonnement wird zum Ende der aktuellen Abrechnungsperiode beendet. Sie können bis dahin weiterhin alle Features nutzen.'
                  : 'Your subscription will be cancelled at the end of the current billing period. You can continue using all features until then.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCancelModal(false)} className="flex-1">
                {language === 'de' ? 'Abbrechen' : 'Cancel'}
              </Button>
              <Button onClick={handleCancelSubscription} disabled={loading} variant="destructive" className="flex-1">
                {loading ? (language === 'de' ? 'Wird gekündigt...' : 'Cancelling...') : (language === 'de' ? 'Kündigen' : 'Confirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Account Modal */}
        <Dialog open={showDeleteModal} onOpenChange={(o) => { if (!deleteLoading) setShowDeleteModal(o); }}>
          <DialogContent className="glass max-w-md" data-testid="delete-account-modal">
            <DialogHeader>
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-rose-500/20 border border-rose-500/30">
                <AlertTriangle className="h-8 w-8 text-rose-400" />
              </div>
              <DialogTitle className="text-center text-rose-400">
                {language === 'de' ? 'Account endgültig löschen?' : 'Permanently delete account?'}
              </DialogTitle>
              <DialogDescription className="text-center">
                {language === 'de'
                  ? 'Diese Aktion ist NICHT umkehrbar. Alle deine Trades, Konten, Tickets, Highlights und KI-Analysen werden für immer entfernt.'
                  : 'This action is NOT reversible. All your trades, accounts, tickets, highlights and AI analyses will be removed forever.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {!(user?.oauth_provider && !user?.has_password) && (
                <div>
                  <Label htmlFor="delete-pwd">{language === 'de' ? 'Passwort zur Bestätigung' : 'Password to confirm'}</Label>
                  <Input
                    id="delete-pwd"
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder={language === 'de' ? 'Dein Passwort' : 'Your password'}
                    className="mt-1"
                    data-testid="delete-account-password-input"
                    autoComplete="current-password"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="delete-confirm">
                  {language === 'de'
                    ? <>Tippe <span className="font-mono text-rose-400">LÖSCHEN</span> zur Bestätigung</>
                    : <>Type <span className="font-mono text-rose-400">DELETE</span> to confirm</>}
                </Label>
                <Input
                  id="delete-confirm"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={language === 'de' ? 'LÖSCHEN' : 'DELETE'}
                  className="mt-1 font-mono"
                  data-testid="delete-account-confirm-input"
                  autoComplete="off"
                />
              </div>
              {deleteError && (
                <p className="text-sm text-rose-400 flex items-center gap-2" data-testid="delete-account-error">
                  <XCircle className="h-4 w-4" /> {deleteError}
                </p>
              )}
            </div>
            <DialogFooter className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading} className="flex-1">
                {language === 'de' ? 'Abbrechen' : 'Cancel'}
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                variant="destructive"
                className="flex-1"
                data-testid="confirm-delete-account-btn"
              >
                {deleteLoading
                  ? (language === 'de' ? 'Wird gelöscht...' : 'Deleting...')
                  : (language === 'de' ? 'Endgültig löschen' : 'Delete forever')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default SettingsPage;
